import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { getAssets } from '../utils/api';
import { Progress } from '@/components/ui/progress';
import NavigationBar from '@/components/NavigationBar';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const VehiclesPage = ({ user }) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState('All India');

  const majorCities = [
    'All India',
    'Mumbai',
    'Delhi',
    'Bangalore',
    'Hyderabad',
    'Chennai',
    'Kolkata',
    'Pune',
    'Ahmedabad',
    'Jaipur',
    'Surat',
    'Lucknow',
    'Kanpur',
    'Nagpur',
    'Indore',
    'Thane',
    'Bhopal',
    'Visakhapatnam',
    'Pimpri-Chinchwad',
    'Patna'
  ];

  useEffect(() => {
    // Check if location is already selected (stored in localStorage)
    const storedCity = localStorage.getItem('selectedCity');
    if (storedCity) {
      setSelectedCity(storedCity);
      loadAssets(storedCity);
    } else {
      // Load all assets by default
      loadAssets('All India');
    }
  }, []);

  const loadAssets = async (city = 'All India') => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/assets`, {
        params: {
          category: 'vehicle',
          city: city
        }
      });
      setAssets(response.data);
    } catch (error) {
      console.error('Failed to load assets', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLocationChange = (e) => {
    const city = e.target.value;
    setSelectedCity(city);
    localStorage.setItem('selectedCity', city);
    loadAssets(city);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <NavigationBar user={user} />

      <div className="max-w-7xl mx-auto px-6 py-20">
        <h1 className="text-5xl font-bold mb-6" data-testid="vehicles-heading">Available Vehicles</h1>
        <p className="text-xl text-gray-600 mb-8">
          You may choose to fractionally invest in any of the available vehicles individually. Each ticket denotes 30 days of usage per year.
        </p>

        {/* Sort/Filter and Location Dropdown Row */}
        <div className="flex justify-between items-center mb-8">
          <div className="text-xl">
            Sort/Filter <span className="text-2xl">›</span>
          </div>
          
          <div className="flex items-center gap-3">
            <label htmlFor="location-select" className="text-gray-700 font-medium">
              Choose Location:
            </label>
            <select
              id="location-select"
              value={selectedCity}
              onChange={handleLocationChange}
              className="px-4 py-2 border-2 border-gray-300 rounded-lg bg-white text-gray-800 font-medium focus:outline-none focus:border-gold cursor-pointer hover:border-gray-400 transition-colors min-w-[180px]"
            >
              {majorCities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </div>
        </div>

        {assets.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-gray-600 mb-4">No vehicles available in {selectedCity}.</p>
            <p className="text-gray-500">Try selecting "All India" to see all available vehicles.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {assets.map((asset) => (
              <div key={asset.id} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl" data-testid={`asset-card-${asset.id}`}>
                <img src={asset.images[0]} alt={asset.name} className="w-full h-64 object-cover" />
                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-2">{asset.name}</h3>
                  <p className="text-gray mb-4">{asset.description.substring(0, 100)}...</p>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span>Tickets Sold</span>
                      <span className="font-semibold">{asset.sold_tickets}/{asset.total_tickets}</span>
                    </div>
                    <Progress value={(asset.sold_tickets / asset.total_tickets) * 100} className="h-2" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-gold text-2xl font-bold">₹{asset.price_per_ticket.toLocaleString()}</span>
                      <span className="text-sm text-gray"> /ticket</span>
                    </div>
                    <Link to={`/asset/${asset.id}`}>
                      <Button className="bg-black text-white hover:bg-black/90" data-testid={`view-details-${asset.id}`}>View Details</Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VehiclesPage;
